/* eslint-disable max-lines */
import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@design-lab/shared/lib/tailwindcss";

import { CSS_COLORS, POPULAR_COLORS } from "./cssColors";

type ColorComboBoxProps = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  size?: "sm" | "md" | "lg";
};

export function ColorComboBox({
  value,
  onChange,
  className,
  placeholder = "white",
  size = "md"
}: ColorComboBoxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredColors, setFilteredColors] = useState(POPULAR_COLORS);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const sizeClasses = {
    sm: "h-8 px-2 text-xs",
    md: "h-9 px-3 text-sm",
    lg: "h-10 px-3 text-sm"
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = useCallback(
    (inputValue: string) => {
      onChange(inputValue);
      setHighlightedIndex(-1);

      // Filter colors based on input with relevance sorting
      if (inputValue.trim()) {
        const searchTerm = inputValue.toLowerCase();
        const matches = CSS_COLORS.filter(
          color =>
            color.name.toLowerCase().includes(searchTerm) ||
            color.hex.toLowerCase().includes(searchTerm)
        );

        // Sort by relevance: exact match > starts with > contains
        const sorted = matches.sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          const aHex = a.hex.toLowerCase();
          const bHex = b.hex.toLowerCase();

          // Exact match has highest priority
          const aExact = aName === searchTerm || aHex === searchTerm ? 0 : 1;
          const bExact = bName === searchTerm || bHex === searchTerm ? 0 : 1;
          if (aExact !== bExact) return aExact - bExact;

          // Starts with has second priority
          const aStarts = aName.startsWith(searchTerm) || aHex.startsWith(searchTerm) ? 0 : 1;
          const bStarts = bName.startsWith(searchTerm) || bHex.startsWith(searchTerm) ? 0 : 1;
          if (aStarts !== bStarts) return aStarts - bStarts;

          // Then alphabetical order
          return aName.localeCompare(bName);
        });

        setFilteredColors(sorted.slice(0, 15));
      } else {
        setFilteredColors(POPULAR_COLORS);
      }

      setIsOpen(true);
    },
    [onChange]
  );

  const handleSelectColor = useCallback(
    (colorName: string) => {
      onChange(colorName);
      setIsOpen(false);
      setHighlightedIndex(-1);
      inputRef.current?.blur();
    },
    [onChange]
  );

  const handleInputFocus = () => {
    setFilteredColors(POPULAR_COLORS);
    setHighlightedIndex(-1);
    setIsOpen(true);
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          setIsOpen(true);
          setHighlightedIndex(0);
        }
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex(prev => (prev < filteredColors.length - 1 ? prev + 1 : prev));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredColors.length) {
          const selectedColor = filteredColors[highlightedIndex];
          if (selectedColor) {
            handleSelectColor(selectedColor.name);
          }
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
      } else if (e.key === "Tab") {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    },
    [isOpen, filteredColors, highlightedIndex, handleSelectColor]
  );

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [highlightedIndex]);

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={e => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className={cn(
            "w-full rounded-lg border border-gray-300 pr-8 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500",
            sizeClasses[size]
          )}
          placeholder={placeholder}
          data-testid="color-combobox-input"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
          data-testid="color-combobox-trigger"
        >
          <ChevronDown className="size-4" />
        </button>
      </div>

      {isOpen && filteredColors.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg"
          data-testid="color-combobox-dropdown"
        >
          {filteredColors.map((color, index) => {
            const isSelected = value.toLowerCase() === color.name.toLowerCase();
            const isHighlighted = index === highlightedIndex;
            return (
              <button
                key={color.name}
                type="button"
                onClick={() => handleSelectColor(color.name)}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100",
                  isSelected && "bg-blue-50",
                  isHighlighted && "bg-gray-100"
                )}
                data-testid={`color-option-${color.name}`}
              >
                <div
                  className="size-4 shrink-0 rounded-full border border-gray-300"
                  style={{ backgroundColor: color.hex }}
                />
                <span className="capitalize">{color.name}</span>
                <span className="ml-auto font-mono text-xs text-gray-500">{color.hex}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
